<template>
  <div class="col-xs-12">
      <transition @before-enter="debriefBeforeEnter" @enter="debriefEnter" @after-enter="debriefAfterEnter">
      <div v-if="questionAnswered && debrief" class="trivia-debrief">
          <div class="card row" style="display: flex;">
              <div class="col-xs-8">
                  <p class="trivia-debrief-text" v-html="debrief.text"></p>
                  <div class="trivia-debrief-link">
                      <template v-if="debrief.link">
                          <resource class="svg-icon" />
                          <a :href="debrief.link" target="_blank" rel="noopener">
                              {{ debrief.link_caption ?? debrief.link }}
                          </a>
                      </template>
                  </div>
              </div>
              <div class="col-xs-4 trivia-debrief-button-container">
                  <div class="trivia-debrief-button">
                      <button ref="continueBtn" class="btn primary-btn" @click="handleContinue">
                          {{ $t("CONTINUE") }} →
                      </button>
                  </div>
              </div>
          </div>
      </div>
      </transition>

    <component
      :is="questionComponent"
      :question="question"
      :question-answered="questionAnswered"
      :correct="correctAnswer"
      :multiple="question.type === 'multiple_choice'"
      @answer="submitAnswer"
      @lightbox="lightboxSrc = $event"
    >
        <div class="trivia-question">
            <div
                v-if="question.image_path"
                class="trivia-image-section"
            >
                <img :src="question.image_path" class="img img-circle" />
                <p
                    class="image-caption"
                    v-html="question.image_caption"
                />
            </div>
            <div class="trivia-question-text" v-html="question.question" @click="handleBodyImageClick"></div>
        </div>
        <div class="trivia-heading">
            <h2>{{ question.heading }}</h2>
        </div>
    </component>

    <div v-if="lightboxSrc" class="trivia-lightbox" @click="lightboxSrc = null">
      <img :src="lightboxSrc" @click.stop />
    </div>
  </div>
</template>
<!----------------------------------------------------------------------------->
<script>
import log from "src/services/logger";
import Resource from "Components/Svg/Resource";
import { ejectAffirmationParticle } from "./affirmationParticles";
import TriviaChoice from "./TriviaChoice";
import TriviaScratchOut from "./TriviaScratchOut";
import TriviaReorder from "./TriviaReorder";

const logTag = "TriviaQuestion";

const QUESTION_COMPONENTS = {
  choice: TriviaChoice,
  multiple_choice: TriviaChoice,
  scratch_out: TriviaScratchOut,
  reorder: TriviaReorder,
};

export default {
  name: "TriviaQuestion",

  components: { Resource, TriviaChoice, TriviaScratchOut, TriviaReorder },

  props: {
    question: { type: Object, required: true },
  },

  data() {
    return {
      questionAnswered: false,
      correctAnswer: null,
      isCorrect: false,
      debrief: null,
      lightboxSrc: null,
    };
  },

  watch: {
    lightboxSrc(val) {
      if (val) {
        document.addEventListener('keydown', this.handleKeyDown);
      } else {
        document.removeEventListener('keydown', this.handleKeyDown);
      }
    },
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },

  computed: {
    questionComponent() {
      return QUESTION_COMPONENTS[this.question.type];
    },
  },

  methods: {
    debriefBeforeEnter(el) {
      el.style.overflow = 'hidden';
      el.style.maxHeight = '0';
      el.style.opacity = '0';
      el.style.transform = 'translateY(-30px)';
    },
    debriefEnter(el, done) {
      const fullHeight = el.scrollHeight;
      // Step 1: slide content down by expanding height (content invisible)
      requestAnimationFrame(() => {
        el.style.transition = 'max-height 0.3s ease-out';
        el.style.maxHeight = fullHeight + 'px';
      });
      // Step 2: after height expansion + 1s pause, slide in and fade in the debrief
      const onHeightDone = () => {
        setTimeout(() => {
          el.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          el.addEventListener('transitionend', done, { once: true });
        }, 250);
      };
      el.addEventListener('transitionend', onHeightDone, { once: true });
    },
    debriefAfterEnter(el) {
      el.style.maxHeight = '';
      el.style.overflow = '';
      el.style.transition = '';
      el.style.opacity = '';
      el.style.transform = '';
    },
    submitAnswer(answer) {
      this.$store
        .dispatch("answerTriviaQuestion", {
          questionId: this.question.id,
          answer: answer,
        })
        .then((data) => {
          this.questionAnswered = true;
          this.correctAnswer = data.correct_ids ?? data.correct_id;
          this.isCorrect = data.correct;
          this.debrief = data.debrief;
        })
        .catch((reason) => {
          log.log(logTag, "Answer trivia error, reason follows:");
          log.log(logTag, reason);
        });
    },

    handleBodyImageClick(event) {
      if (event.target.tagName === 'IMG') {
        this.lightboxSrc = event.target.src;
      }
    },

    handleKeyDown(event) {
      if (event.key === 'Escape') this.lightboxSrc = null;
    },

    handleContinue() {
      if (this.isCorrect && Math.random() < 0.5) {
        ejectAffirmationParticle(this.$refs.continueBtn);
      }
      this.$emit("next");
    },
  },
};
</script>
<!----------------------------------------------------------------------------->
<style lang="scss" type="text/scss">
.trivia-question-text {
  h1:has(> audio),
  h1:has(> video),
  h1:has(> img) {
    font: inherit;
  }

  audio,
  video {
    display: block;
    width: 100%;
  }

  img {
    max-width: 100%;
    height: auto;
    cursor: zoom-in;
  }
}

.trivia-lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  cursor: zoom-out;

  img {
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
    cursor: default;
  }
}
</style>
